import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { X, ArrowUpRight, Phone, MapPin, Images, Wind } from "lucide-react";
import { TopographicBackground, PaperGrain, GoldOrb, StarField } from "./TopographicBackground";
import { ArtDivider } from "./ArtDivider";
import { useSiteContent } from "../lib/siteContent";

const EASE = [0.22, 1, 0.36, 1];

function LandmarkCard({ door, index, onReturn, callUrl }) {
  const isCurrent = door.current;
  const body = (
    <>
      <div className="flex items-baseline justify-between">
        <span className="font-display text-5xl font-light text-gold-deep/80 num-old">{door.index}</span>
        <span className="kicker text-ink-muted">
          {door.marker} · {door.distance}
        </span>
      </div>

      <h3 className="headline mt-6 text-[28px] font-medium text-ink sm:text-[30px]">{door.name}</h3>
      <p className="deck mt-2 text-lg sm:text-xl">{door.tagline}</p>

      {door.description && (
        <p className="prose-editorial mt-4 text-[16px] leading-relaxed">{door.description}</p>
      )}

      {door.teaser && (
        <p
          className="mt-4 flex gap-2 text-[15px] italic leading-relaxed text-gold-deep"
          data-testid={`ecosystem-teaser-${door.index}`}
          style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}
        >
          <span aria-hidden className="not-italic text-gold-foil">—</span>
          {door.teaser}
        </p>
      )}

      <div className="mt-6 flex items-center justify-between">
        {door.detail ? (
          <span className="kicker text-gold-deep">{door.detail}</span>
        ) : (
          <span className="kicker flex items-center gap-1.5 text-ink-muted">
            <MapPin strokeWidth={1.4} className="h-3.5 w-3.5" /> You are here
          </span>
        )}

        {isCurrent ? (
          <button
            type="button"
            onClick={onReturn}
            data-testid="ecosystem-return-book"
            className="inline-flex items-center gap-1.5 rounded-full border border-gold/40 px-4 py-1.5 font-display text-sm text-ink transition hover:bg-gold/10"
          >
            Return to the book
          </button>
        ) : (
          <span className="inline-flex items-center gap-1.5 font-display text-[15px] text-gold-deep transition group-hover:gap-2.5">
            {door.cta_label} <ArrowUpRight strokeWidth={1.6} className="h-4 w-4" />
          </span>
        )}
      </div>
    </>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 22 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.1 + index * 0.08, ease: EASE }}
      className="flex h-full flex-col"
    >
      {isCurrent ? (
        <div
          data-testid={`ecosystem-door-${door.index}`}
          className="debossed group relative block flex-1 rounded-2xl p-7 ring-1 ring-gold/30"
        >
          {body}
        </div>
      ) : (
        <>
          <Link
            to={door.url}
            onClick={onReturn}
            data-testid={`ecosystem-door-${door.index}`}
            className="debossed group relative block flex-1 rounded-2xl p-7 transition-transform duration-500 hover:-translate-y-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold/60"
          >
            {body}
          </Link>
          {callUrl && (
            <a
              href={callUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`ecosystem-door-call-${door.index}`}
              className="mt-3 inline-flex items-center gap-1.5 self-start pl-1 text-[13px] text-ink-muted underline-offset-4 transition hover:text-gold-deep hover:underline"
            >
              <Phone strokeWidth={1.4} className="h-3.5 w-3.5" />
              Unsure? Book a free Braced Call first
            </a>
          )}
        </>
      )}
    </motion.div>
  );
}

export const Ecosystem = ({ open, onClose, book }) => {
  const site = useSiteContent();
  const doors = book?.ecosystem || [];
  const callUrl = book?.links?.braced_call;
  const galleryUrl = book?.links?.gallery;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[90] overflow-y-auto"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
          data-testid="ecosystem-overlay"
        >
          <div className="fixed inset-0 watercolor-wash" />
          <PaperGrain />
          <GoldOrb className="left-1/2 top-[-120px] -translate-x-1/2" size={560} testId="eco-orb" />
          <StarField count={16} className="opacity-60" />
          <TopographicBackground
            className="pointer-events-none absolute inset-x-0 top-0 h-[520px] w-full"
            opacity={0.5}
            animate
          />

          <div className="relative z-10 mx-auto max-w-5xl px-6 py-10 sm:px-10 sm:py-16">
            <div className="mb-12 flex items-start justify-between sm:mb-16">
              <div>
                <span className="kicker text-gold-foil">The Ecosystem</span>
                <span className="kicker mt-2 block text-ink-muted">A guided map of the work</span>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                data-testid="close-ecosystem-btn"
                className="debossed-btn flex h-10 w-10 flex-none items-center justify-center rounded-full text-ink-soft focus:outline-none focus-visible:ring-2 focus-visible:ring-gold/60"
              >
                <X strokeWidth={1.4} className="h-5 w-5" />
              </button>
            </div>

            <motion.h2
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: EASE }}
              className="headline max-w-2xl text-[46px] leading-[0.98] text-ink sm:text-[64px]"
            >
              {site?.ecosystem?.heading || "Four doors into the work"}
            </motion.h2>
            <div className="gold-rule my-8 max-w-[180px]" />
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.9, delay: 0.15 }}
              className="deck max-w-xl text-xl sm:text-[22px]"
            >
              {site?.ecosystem?.intro ||
                "A living map of the journey. Each landmark names a stage of the work — enter the one that meets you where you are."}
            </motion.p>

            <div className="mt-14 grid grid-cols-1 items-stretch gap-6 md:grid-cols-2">
              {doors.map((door, i) => (
                <LandmarkCard
                  key={door.index}
                  door={door}
                  index={i}
                  onReturn={onClose}
                  callUrl={callUrl}
                />
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.38, ease: EASE }}
              className="mt-8"
            >
              <Link
                to="/somatic-reset"
                onClick={onClose}
                data-testid="ecosystem-somatic-reset-link"
                className="debossed group flex flex-col items-start gap-4 rounded-2xl p-7 ring-1 ring-gold/25 transition-transform duration-500 hover:-translate-y-1 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="flex items-start gap-4">
                  <span className="mt-0.5 flex h-11 w-11 flex-none items-center justify-center rounded-full ring-1 ring-gold/40">
                    <Wind strokeWidth={1.4} className="h-5 w-5 text-gold-deep" />
                  </span>
                  <div>
                    <span className="kicker text-gold-foil">A gentle first step · Free</span>
                    <h3 className="headline mt-1.5 text-[26px] text-ink">The 1-Minute Somatic Reset</h3>
                    <p className="deck mt-1 text-lg">Notice where you're bracing, soften what you can — a free pocket guide, instant download.</p>
                  </div>
                </div>
                <span className="inline-flex items-center gap-1.5 whitespace-nowrap font-display text-[15px] text-gold-deep transition group-hover:gap-2.5">
                  Get the guide <ArrowUpRight strokeWidth={1.6} className="h-4 w-4" />
                </span>
              </Link>
            </motion.div>

            {galleryUrl && (
              <motion.div
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.42, ease: EASE }}
                className="mt-8"
              >
                <Link
                  to={galleryUrl}
                  onClick={onClose}
                  data-testid="ecosystem-gallery-link"
                  className="debossed group flex flex-col items-start gap-4 rounded-2xl p-7 transition-transform duration-500 hover:-translate-y-1 sm:flex-row sm:items-center sm:justify-between"
                >
                <div className="flex items-start gap-4">
                  <span className="mt-0.5 flex h-11 w-11 flex-none items-center justify-center rounded-full ring-1 ring-gold/40">
                    <Images strokeWidth={1.4} className="h-5 w-5 text-gold-deep" />
                  </span>
                  <div>
                    <span className="kicker text-ink-muted">An Overlook · 4.0 mi</span>
                    <h3 className="headline mt-1.5 text-[26px] text-ink">The Virtual Art Gallery</h3>
                    <p className="deck mt-1 text-lg">Wander the imagery and visual world of the work.</p>
                  </div>
                </div>
                <span className="inline-flex items-center gap-1.5 whitespace-nowrap font-display text-[15px] text-gold-deep transition group-hover:gap-2.5">
                  Enter the gallery <ArrowUpRight strokeWidth={1.6} className="h-4 w-4" />
                </span>
                </Link>
              </motion.div>
            )}

            {callUrl && (
              <motion.div
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.45, ease: EASE }}
                className="mt-8 flex flex-col items-center gap-4 text-center"
              >
                <ArtDivider className="w-full" />
                <span className="kicker text-gold-foil">A gentle first step</span>
                <p className="headline text-3xl font-light text-ink sm:text-[40px]">
                  Not sure which door is yours?
                </p>
                <p className="deck max-w-md text-lg">
                  Share what feels heavy and receive clear guidance on the path that fits — no cost,
                  no pressure.
                </p>
                <a
                  href={callUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="ecosystem-braced-call"
                  className="debossed-btn mt-2 inline-flex items-center gap-2.5 rounded-full px-8 py-3.5 font-display text-lg text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-gold/60"
                >
                  <Phone strokeWidth={1.4} className="h-4 w-4 text-gold-deep" />
                  Book a free Braced Call
                </a>
              </motion.div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
